import React from 'react';
import { Button, DialogActions, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { ModalBase } from 'src/components/ConfirmationModal';
import TextField from 'src/components/TextField';

const MAX_FOLDER_NAME_LENGTH = 128;

interface FolderNameDialogProps {
  open: boolean;
  titleKey: string;
  submitKey: string;
  // shown above the name field, e.g. where a new sub-folder will be created
  description?: string;
  initialName?: string;
  isSubmitting?: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (name: string) => void;
}

export default function FolderNameDialog({
  open,
  titleKey,
  submitKey,
  description,
  initialName = '',
  isSubmitting = false,
  onClose,
  onSubmit,
}: FolderNameDialogProps) {
  const { t } = useTranslation('lca');
  const [name, setName] = React.useState(initialName);

  React.useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmedName = name.trim();
  const isValid =
    trimmedName.length > 0 && trimmedName.length <= MAX_FOLDER_NAME_LENGTH;

  const submit = () => {
    if (!isValid || isSubmitting) return;
    onSubmit(trimmedName);
  };

  return (
    <ModalBase open={open} onClose={onClose} titleKey={titleKey}>
      {!!description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: '20px' }}
        >
          {description}
        </Typography>
      )}
      <TextField
        label={t('folders.nameLabel')}
        placeholder={t('folders.namePlaceholder')}
        value={name}
        autoFocus
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        sx={{ width: '100%', minWidth: '360px' }}
      />
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          paddingBottom: 0,
        }}
      >
        <Button
          onClick={onClose}
          variant="secondary"
          sx={{ minWidth: '200px' }}
        >
          <Trans i18nKey="common.cancel" />
        </Button>
        <Button
          onClick={submit}
          disabled={!isValid || isSubmitting}
          sx={{ minWidth: '200px' }}
        >
          <Trans i18nKey={submitKey} />
        </Button>
      </DialogActions>
    </ModalBase>
  );
}
